import { Controller } from "@hotwired/stimulus";

// Shared global counter for open dialogs (modals and slideovers)
// This ensures proper scrollbar compensation when mixing modals and slideovers
if (!window.__openDialogCount) {
  window.__openDialogCount = 0;
}

// Reset dialog count on page navigation to prevent desync issues
// This handles cases where users navigate back/forward while dialogs are open
if (!window.__dialogCountResetBound) {
  window.__dialogCountResetBound = true;

  const resetDialogCount = () => {
    // Only reset if no dialogs are actually open
    const openDialogs = document.querySelectorAll("dialog[open]");
    if (openDialogs.length === 0) {
      window.__openDialogCount = 0;
      document.documentElement.style.removeProperty("--scrollbar-compensation");
      document.body.classList.remove("modal-open", "slideover-open");
    } else {
      // Sync count with actual open dialogs
      window.__openDialogCount = openDialogs.length;
    }
  };

  document.addEventListener("turbo:before-cache", resetDialogCount);
  document.addEventListener("turbo:load", resetDialogCount);
}

export default class extends Controller {
  static targets = ["dialog", "template"];
  static values = {
    open: { type: Boolean, default: false }, // Whether the modal is open
    lazyLoad: { type: Boolean, default: false }, // Whether to lazy load the modal content
    turboFrameSrc: { type: String, default: "" }, // URL for the turbo frame
    preventDismiss: { type: Boolean, default: false }, // Whether to prevent the modal from being dismissed
    autoFocus: { type: Boolean, default: false }, // True: focus first focusable element on open. False: don't focus on anything by default. (if an element has autofocus="true", it will focus on it whether this option is true or false)
  };

  connect() {
    // Initialize state
    this.contentLoaded = false;
    this.isBouncing = false;
    this.isOpen = false; // Track if this specific modal is open

    // Initialize focus trapping
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;

    // Detect if we're using a div or dialog element
    this.isDialog = this.dialogTarget.tagName.toLowerCase() === "dialog";

    // Check if dialog is already open (from previous page state) and close it
    if (this.isDialog && this.dialogTarget.open) {
      // Mark as open temporarily to allow cleanup
      this.isOpen = true;
      this.dialogTarget.close();
      this.cleanupScrollbarCompensation();
    } else if (!this.isDialog && this.dialogTarget.classList.contains("modal-open")) {
      // For div-based modals, check if they have the open class
      this.isOpen = true;
      this.hideDivModal();
      this.cleanupScrollbarCompensation();
    }

    // Now handle the intended open state
    if (this.openValue) this.open();

    // Set up event listeners
    this.boundBeforeCache = this.beforeCache.bind(this);
    this.boundBeforeVisit = this.beforeVisit.bind(this);
    document.addEventListener("turbo:before-cache", this.boundBeforeCache);
    document.addEventListener("turbo:before-visit", this.boundBeforeVisit);

    if (this.isDialog) {
      // Add event listener for when dialog is closed by any means (including Escape key)
      this.dialogTarget.addEventListener("close", this.handleDialogClose.bind(this));

      // Prevent Escape key from closing if preventDismiss is true
      this.dialogTarget.addEventListener("cancel", this.handleDialogCancel.bind(this));
    }

    // Additional keydown listener for better escape key handling
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.dialogTarget.addEventListener("keydown", this.boundHandleKeydown);

    // For div-based modals, add global escape key listener
    if (!this.isDialog) {
      this.boundHandleGlobalKeydown = this.handleGlobalKeydown.bind(this);
      document.addEventListener("keydown", this.boundHandleGlobalKeydown);
    }
  }

  disconnect() {
    // If this modal was open when disconnecting, clean up scrollbar compensation
    if (this.isOpen) {
      this.cleanupScrollbarCompensation();
    }

    document.removeEventListener("turbo:before-cache", this.boundBeforeCache);
    document.removeEventListener("turbo:before-visit", this.boundBeforeVisit);

    if (this.isDialog) {
      this.dialogTarget.removeEventListener("close", this.handleDialogClose.bind(this));
      this.dialogTarget.removeEventListener("cancel", this.handleDialogCancel.bind(this));
    }

    this.dialogTarget.removeEventListener("keydown", this.boundHandleKeydown);

    if (!this.isDialog && this.boundHandleGlobalKeydown) {
      document.removeEventListener("keydown", this.boundHandleGlobalKeydown);
    }
  }

  async open() {
    // If already open, don't do anything
    if (this.isOpen) return;

    // If lazy loading is enabled and content hasn't been loaded yet, load it now
    if (this.lazyLoadValue && !this.contentLoaded) {
      await this.#loadTemplateContent();
      this.contentLoaded = true;
    }

    // Increment the counter and mark this modal as open
    window.__openDialogCount++;
    this.isOpen = true;

    // Only apply scrollbar compensation if this is the first dialog
    if (window.__openDialogCount === 1) {
      const scrollbarWidth = this.getScrollbarWidth();
      if (scrollbarWidth > 0) {
        // Set CSS custom property for scrollbar width
        document.documentElement.style.setProperty("--scrollbar-compensation", `${scrollbarWidth}px`);
        // Add class to body to trigger compensation
        document.body.classList.add("modal-open");
      }
    }

    if (this.isDialog) {
      this.dialogTarget.showModal();
    } else {
      this.showDivModal();
    }

    // Set up focus trapping
    this.setupFocusTrapping();

    // On touch devices, remove initial focus to prevent awkward focus rings
    // But respect native autofocus attributes
    if (this.isTouchDevice()) {
      // Find the currently focused element within the dialog
      const focusedElement = this.dialogTarget.querySelector(":focus");
      if (focusedElement && !focusedElement.hasAttribute("autofocus")) {
        // Only blur if it doesn't have native autofocus
        focusedElement.blur();
      }
    }
  }

  // Allows for a closing animation since display transitions don't work yet
  close() {
    // If not open, don't do anything
    if (!this.isOpen) return;

    this.dialogTarget.setAttribute("closing", "");

    Promise.all(this.dialogTarget.getAnimations().map((animation) => animation.finished)).then(() => {
      this.dialogTarget.removeAttribute("closing");

      if (this.isDialog) {
        this.dialogTarget.close();
        // The handleDialogClose method will handle the cleanup when the dialog actually closes
      } else {
        // For div modals, start the hide animation but delay cleanup
        this.hideDivModal();
        // Wait for the fade-out animation to complete before cleaning up scrollbar
        setTimeout(() => {
          this.handleDialogClose();
        }, 200); // Match this with the CSS transition duration
      }

      // Reset bouncing flag
      this.isBouncing = false;
    });
  }

  backdropClose(event) {
    // For dialog elements: only close if clicking directly on the dialog element (the backdrop)
    // For div elements: check if clicking on the backdrop wrapper
    const isBackdropClick = this.isDialog
      ? event.target.nodeName === "DIALOG"
      : event.target === this.dialogTarget || event.target.hasAttribute("data-modal-backdrop");

    if (isBackdropClick) {
      // Stop propagation to prevent closing parent dialogs
      event.stopPropagation();

      if (this.preventDismissValue) {
        this.bounce();
      } else {
        this.close();
      }
    }
  }

  // For showing non-modally
  show() {
    this.dialogTarget.show();
  }

  hide() {
    this.close();
  }

  beforeCache() {
    // Close immediately without animation during navigation
    if (this.isOpen) {
      this.dialogTarget.removeAttribute("closing");
      if (this.isDialog) {
        this.dialogTarget.close();
      } else {
        this.hideDivModal();
      }
      this.cleanupScrollbarCompensation();
    }
  }

  beforeVisit() {
    // Close immediately without animation during navigation
    if (this.isOpen) {
      this.dialogTarget.removeAttribute("closing");
      if (this.isDialog) {
        this.dialogTarget.close();
      } else {
        this.hideDivModal();
      }
      this.cleanupScrollbarCompensation();
    }
  }

  // Calculate actual scrollbar width
  getScrollbarWidth() {
    // Create a temporary div to measure scrollbar width
    const outer = document.createElement("div");
    outer.style.visibility = "hidden";
    outer.style.overflow = "scroll";
    outer.style.msOverflowStyle = "scrollbar"; // Force scrollbars on IE/Edge
    document.body.appendChild(outer);

    const inner = document.createElement("div");
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode.removeChild(outer);

    return scrollbarWidth;
  }

  async #loadTemplateContent() {
    // Find the container in the dialog to append content to
    const container = this.dialogTarget.querySelector("[data-modal-content]") || this.dialogTarget;

    // Check if we should use Turbo Frame lazy loading
    if (this.turboFrameSrcValue) {
      // Look for a turbo-frame in the container
      let turboFrame = container.querySelector("turbo-frame");

      if (!turboFrame) {
        // Create a turbo-frame if it doesn't exist
        turboFrame = document.createElement("turbo-frame");
        turboFrame.id = "modal-lazy-content";

        // Clear any loading indicators or placeholder content
        container.innerHTML = "";
        container.appendChild(turboFrame);
      }

      // Set the src to trigger the lazy load
      turboFrame.src = this.turboFrameSrcValue;

      // Wait for the turbo-frame to load
      return new Promise((resolve) => {
        const handleLoad = () => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          resolve();
        };

        turboFrame.addEventListener("turbo:frame-load", handleLoad);

        // Fallback timeout in case the frame doesn't load
        setTimeout(() => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          resolve();
        }, 5000);
      });
    } else if (this.hasTemplateTarget) {
      // Use template-based lazy loading (existing behavior)
      const templateContent = this.templateTarget.content.cloneNode(true);

      // Clear any loading indicators or placeholder content
      container.innerHTML = "";

      // Append the template content
      container.appendChild(templateContent);
    }
  }

  // Handle cleanup when dialog is closed by any means
  handleDialogClose() {
    // If the modal was open, decrement the counter
    if (this.isOpen) {
      this.cleanupScrollbarCompensation();
    }

    // Ensure the closing attribute is removed
    this.dialogTarget.removeAttribute("closing");

    // Reset bouncing flag
    this.isBouncing = false;
  }

  // Centralized method to handle scrollbar compensation cleanup
  cleanupScrollbarCompensation() {
    if (!this.isOpen) return;

    window.__openDialogCount = Math.max(0, window.__openDialogCount - 1);
    this.isOpen = false;

    // Only remove scrollbar compensation if no dialogs are open
    if (window.__openDialogCount === 0) {
      document.documentElement.style.removeProperty("--scrollbar-compensation");
      document.body.classList.remove("modal-open", "slideover-open");
    }
  }

  // Handle keydown events
  handleKeydown(event) {
    if (event.key === "Escape" && this.preventDismissValue) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.bounce();
      return false;
    }

    // Handle Tab key for focus trapping
    if (event.key === "Tab") {
      this.handleTabKey(event);
    }
  }

  // Handle cancel event (Escape key)
  handleDialogCancel(event) {
    if (this.preventDismissValue) {
      event.preventDefault();
      event.stopPropagation();
      this.bounce();
      return false;
    }
  }

  // Add bounce animation to indicate modal won't close
  bounce() {
    // Prevent multiple bounces in quick succession
    if (this.isBouncing) return;

    this.isBouncing = true;

    // For a more pronounced effect, we can combine with scale
    this.dialogTarget.classList.add("scale-105", "transition-transform");

    setTimeout(() => {
      this.dialogTarget.classList.remove("scale-105");
      this.dialogTarget.classList.add("scale-100");

      setTimeout(() => {
        // Remove all animation classes
        this.dialogTarget.classList.remove("scale-100", "transition-transform");

        // Allow bouncing again after a short cooldown
        setTimeout(() => {
          this.isBouncing = false;
        }, 200); // Additional cooldown to prevent rapid bouncing
      }, 150);
    }, 150);
  }

  // Check if the device supports touch
  isTouchDevice() {
    return "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
  }

  // Set up focus trapping for the modal
  setupFocusTrapping() {
    // Get all focusable elements within the modal
    this.updateFocusableElements();

    // Check for elements with native autofocus attribute first
    const autofocusElement = this.dialogTarget.querySelector("[autofocus]");
    if (autofocusElement) {
      // Always respect native autofocus, regardless of device type
      autofocusElement.focus();
    } else if (this.autoFocusValue && this.firstFocusableElement && !this.isTouchDevice()) {
      // Fall back to auto-focusing first element if no native autofocus and not on touch device
      this.firstFocusableElement.focus();
    }
  }

  // Update the list of focusable elements
  updateFocusableElements() {
    const focusableSelector = [
      "a[href]",
      "area[href]",
      'input:not([disabled]):not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      "details",
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable]:not([contenteditable="false"])',
    ].join(",");

    this.focusableElements = Array.from(this.dialogTarget.querySelectorAll(focusableSelector)).filter((element) => {
      // Filter out elements that are not visible or have display: none
      return (
        element.offsetWidth > 0 &&
        element.offsetHeight > 0 &&
        getComputedStyle(element).display !== "none" &&
        getComputedStyle(element).visibility !== "hidden"
      );
    });

    this.firstFocusableElement = this.focusableElements[0] || null;
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1] || null;
  }

  // Handle Tab key for focus trapping
  handleTabKey(event) {
    // Update focusable elements in case the DOM has changed
    this.updateFocusableElements();

    if (this.focusableElements.length === 0) {
      // If no focusable elements, prevent tab and keep focus on dialog
      event.preventDefault();
      return;
    }

    if (this.focusableElements.length === 1) {
      // If only one focusable element, prevent tab and keep focus on it
      event.preventDefault();
      this.firstFocusableElement.focus();
      return;
    }

    // Handle normal tab navigation with wrapping
    if (event.shiftKey) {
      // Shift+Tab: moving backwards
      if (document.activeElement === this.firstFocusableElement) {
        event.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      // Tab: moving forwards
      if (document.activeElement === this.lastFocusableElement) {
        event.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }

  // Show div-based modal
  showDivModal() {
    this.dialogTarget.classList.add("modal-open");
    this.dialogTarget.style.display = "flex";
    // Force a reflow to ensure the display change is applied before the opacity transition
    this.dialogTarget.offsetHeight;
    this.dialogTarget.classList.add("modal-visible");
  }

  // Hide div-based modal
  hideDivModal() {
    this.dialogTarget.classList.remove("modal-visible");
    // Wait for the fade-out animation before hiding and removing classes
    setTimeout(() => {
      this.dialogTarget.style.display = "none";
      this.dialogTarget.classList.remove("modal-open");
    }, 200); // Match this with your CSS transition duration
  }

  // Handle global keydown for div-based modals (escape key)
  handleGlobalKeydown(event) {
    // Only handle if this modal is open and is the topmost modal
    if (!this.isOpen) return;

    // Check if this is the topmost modal by comparing with all open modals
    const allOpenModals = document.querySelectorAll('[data-controller*="modal"] [data-modal-target="dialog"].modal-open');
    const isTopmost = allOpenModals.length === 0 || allOpenModals[allOpenModals.length - 1] === this.dialogTarget;

    if (event.key === "Escape" && isTopmost) {
      if (this.preventDismissValue) {
        event.preventDefault();
        event.stopPropagation();
        this.bounce();
      } else {
        event.preventDefault();
        this.close();
      }
    }
  }
}
